import React from 'react';
import cx from 'classnames';
import styles from '../Table.module.scss';
import { TableOptions } from '@/types/table';
import { AdditionalHeaderDefinition } from '@/types/table';
import { ColumnOffsets, getColumnWidthStyle, getStickyColumnProps, PositionedColumnDef } from '../utils/columnLayout';
import { getNextSortingState, getSortPriority } from '../utils/utils';
import { Table } from '@tanstack/react-table';
import SortingIndicator from './SortingIndicator';
import { ColumnResizeHandle } from './ResizeHandles';
import { useHeaderTruncationTooltip } from '../hooks/useHeaderTruncationTooltip';
import Text from '@/storybook/components/Text/Text';

type AdditionalHeadersProps<T> = {
  table: Table<T>;
  options?: TableOptions<T>;
  positionedColumnDefs: PositionedColumnDef<T>[];
  columnOffsets: ColumnOffsets;
};

type CellContentResult = {
  content: React.ReactNode;
  sortable: boolean;
  isSorted: false | 'asc' | 'desc';
};

const isAdditionalHeader = (value: React.ReactNode | AdditionalHeaderDefinition): value is AdditionalHeaderDefinition =>
  value !== null && typeof value === 'object' && 'label' in value && 'enableSorting' in value;

const resolveCellContent = (
  cellData: React.ReactNode | AdditionalHeaderDefinition | null | undefined,
  cell: { column: { getCanSort: () => boolean; getIsSorted: () => false | 'asc' | 'desc' } },
): CellContentResult => {
  const defaultResult: CellContentResult = { content: '', sortable: false, isSorted: false };

  if (typeof cellData === 'string' || React.isValidElement(cellData)) {
    return { ...defaultResult, content: cellData };
  }

  if (!isAdditionalHeader(cellData)) {
    return defaultResult;
  }

  const sortable = cell.column.getCanSort();
  const sortState = cell.column.getIsSorted();
  const isSorted = sortState === 'asc' || sortState === 'desc' ? sortState : false;

  return {
    content: cellData.label ?? '',
    sortable: cellData.enableSorting && sortable,
    isSorted,
  };
};

const isRenderableMap = (
  value: Record<string, React.ReactNode> | null | undefined,
): value is Record<string, React.ReactNode> => value !== null && typeof value === 'object' && !Array.isArray(value);

const resolveDataRenderContent = (
  dataRender: Record<string, React.ReactNode> | null | undefined,
  cellId: string,
): React.ReactNode | undefined => {
  if (!isRenderableMap(dataRender)) {
    return undefined;
  }

  return dataRender[cellId];
};

const AdditionalHeaders = <T,>({ table, options, positionedColumnDefs, columnOffsets }: AdditionalHeadersProps<T>) => {
  const { tooltipContext, resolvedTooltipPlace, handleHeaderMouseEnter, handleHeaderMouseLeave } =
    useHeaderTruncationTooltip({ tooltipPlace: options?.tooltipPlace });

  if (!options?.additionalHeaderRows) return null;

  const showSortingPriorityBadge = !!options?.showSortingPriorityBadge;
  const isResizing = !!table.getState().columnSizingInfo.isResizingColumn;
  const [firstHeaderGroup] = table.getHeaderGroups();
  const headerCells = firstHeaderGroup?.headers?.filter(headerCell => headerCell.column.getIsVisible()) ?? [];
  return (
    <>
      {options.additionalHeaderRows.map((additionalRow, index) => (
        <tr key={`additional-header-row-${index}`} className={styles.additionalHeader} style={additionalRow.styles}>
          {headerCells.map((cell, iAdditionalCells) => {
            const columnDef = positionedColumnDefs[iAdditionalCells];
            const {
              isStickyColumnLeft,
              isStickyColumnRight,
              isLastStickyColumn,
              isFirstStickyRightColumn,
              left,
              right,
            } = getStickyColumnProps(positionedColumnDefs, columnOffsets, iAdditionalCells);
            const widthStyle = getColumnWidthStyle(columnDef?.size);
            let cellContent: React.ReactNode = '';
            let sortable = false;
            let isSorted: false | 'asc' | 'desc' = false;

            const renderedCellContent = resolveDataRenderContent(additionalRow.dataRender, cell.id);

            if (renderedCellContent !== undefined) {
              cellContent = renderedCellContent;
            } else if (additionalRow.data) {
              const cellData = additionalRow.data[cell.id];
              const result = resolveCellContent(cellData, cell);
              cellContent = result.content;
              sortable = result.sortable;
              isSorted = result.isSorted;
            }

            const additionHeaderRowsClassName = cx({
              [styles.sticky]: isStickyColumnLeft,
              [styles.stickyRight]: isStickyColumnRight,
              [styles.lastStickyColumn]: isLastStickyColumn,
              [styles.firstStickyRightColumn]: isFirstStickyRightColumn,
              [styles.sortable]: sortable,
              [styles.sorted]: isSorted !== false,
            });

            const style = {
              ...(additionalRow.styles ?? {}),
              width: widthStyle,
              minWidth: widthStyle,
              maxWidth: widthStyle,
              left,
              right,
            };
            const sortPriority =
              sortable && showSortingPriorityBadge
                ? getSortPriority(table.getState().sorting, cell.column.id)
                : undefined;
            const sortDescFirst = cell.column.getFirstSortDir() === 'desc';
            const handleSortClick = sortable
              ? () =>
                  table.setSorting(currentSorting => getNextSortingState(currentSorting, cell.column.id, sortDescFirst))
              : undefined;

            const isLastRow = index === options.additionalHeaderRows.length - 1;
            const hasMultipleRows = options.additionalHeaderRows.length > 1;
            const showResizeHandle = !(isLastRow && hasMultipleRows) && cell.column.getCanResize();

            return (
              <th
                key={cell.id}
                className={additionHeaderRowsClassName}
                style={style}
                onClick={handleSortClick}
                scope="col"
              >
                <div
                  className={cx(styles.headerCellContent, {
                    [styles.sortable]: sortable,
                    [styles.sorted]: isSorted !== false,
                    [styles.regularFontWeight]: index === 0,
                  })}
                  style={isResizing ? { pointerEvents: 'none' } : undefined}
                >
                  <Text
                    as="span"
                    className={styles.headerCellText}
                    data-tooltip-id={tooltipContext?.id}
                    data-tooltip-hidden={true}
                    data-tooltip-place={resolvedTooltipPlace}
                    onMouseEnter={handleHeaderMouseEnter}
                    onMouseLeave={handleHeaderMouseLeave}
                  >
                    {cellContent}
                  </Text>
                  {sortable && <SortingIndicator isSorted={isSorted} priority={sortPriority} />}
                </div>
                {showResizeHandle && <ColumnResizeHandle header={cell} />}
              </th>
            );
          })}
        </tr>
      ))}
    </>
  );
};

export default AdditionalHeaders;
